import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, collection, getDocs, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

const MissionalScale = ({ 
  discipleId, 
  currentUser, 
  initialValue = -5, 
  onChange 
}) => {
  const [value, setValue] = useState(initialValue);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [scaleInfoMap, setScaleInfoMap] = useState({});
  const [loadingDescriptions, setLoadingDescriptions] = useState(true);
  const [sortedLevels, setSortedLevels] = useState([]);

  // Load initial scale value from the database
  useEffect(() => {
    const loadScaleValue = async () => {
      if (!discipleId || !currentUser?.appID) {
        console.error('Missing required props:', { discipleId, currentUserAppID: currentUser?.appID });
        setError('Unable to load: Missing user information');
        return;
      }

      try {
        const mentorDetailsRef = doc(
          db, 
          'disciples', 
          discipleId, 
          'mentorDetails', 
          currentUser.appID
        );
        
        const docSnap = await getDoc(mentorDetailsRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          if (data.missionalScale !== undefined) {
            setValue(data.missionalScale);
          }
        }
      } catch (err) {
        console.error('Error loading missional scale value:', err);
        setError('Failed to load scale value');
      }
    };

    if (discipleId && currentUser?.appID) {
      loadScaleValue();
    }
  }, [discipleId, currentUser]);

  // Fetch scale descriptions AND life application from Firestore
  useEffect(() => {
    const fetchDescriptions = async () => {
      setLoadingDescriptions(true);
      try {
        const scaleLevelsRef = collection(db, 'missionalScaleLevels');
        const querySnapshot = await getDocs(scaleLevelsRef);
        const descriptions = {};
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          if (data.level !== undefined) { 
            descriptions[data.level] = {
              phrase: data.phrase || 'No phrase defined',
              description: data.description || 'No description defined',
              lifeApplication: data.lifeApplication || ''
            };
          }
        });
        setScaleInfoMap(descriptions);
        const levels = Object.keys(descriptions).map(Number).sort((a, b) => a - b);
        setSortedLevels(levels);
        setError(null);
      } catch (err) {
        console.error("Error fetching missional scale descriptions:", err);
        setError("Failed to load scale descriptions.");
        setScaleInfoMap({});
        setSortedLevels([]);
      } finally {
        setLoadingDescriptions(false);
      }
    };

    fetchDescriptions();
  }, []);

  // NEW: Reusable function to save the scale value
  const saveScaleValue = async (newValue) => {
    // Ensure newValue is an integer
    const valueToSave = parseInt(newValue);
    if (isNaN(valueToSave)) {
      console.error('Invalid value passed to saveScaleValue:', newValue);
      return;
    }

    if (!discipleId || !currentUser?.appID) {
      console.error('Missing required props for save:', { discipleId, currentUserAppID: currentUser?.appID });
      setError('Unable to save: Missing user information');
      return;
    }

    setIsSaving(true);
    setError(null);
    try {
      const mentorDetailsRef = doc(db, 'disciples', discipleId, 'mentorDetails', currentUser.appID);
      await setDoc(mentorDetailsRef, {
        missionalScale: valueToSave,
        lastUpdated: serverTimestamp()
      }, { merge: true });

      // Call the parent's onChange if provided
      if (onChange) {
        onChange(valueToSave);
      }
      console.log('Missional scale saved successfully:', valueToSave);
    } catch (err) {
      console.error('Error saving missional scale value:', err);
      setError('Failed to save scale value');
      // Optionally revert optimistic update or show toast
    } finally {
      setIsSaving(false);
    }
  };

  // Handle scale value changes FROM SLIDER
  const handleChange = async (e) => {
    const newValue = parseInt(e.target.value);
    setValue(newValue); // Optimistic UI update
    await saveScaleValue(newValue); // Call reusable save function
  };

  // NEW: Handle clicks on the level descriptions
  const handleLevelClick = async (level) => {
    setValue(level); // Optimistic UI update
    await saveScaleValue(level); // Call reusable save function
  };

  // Get current descriptions based on slider value
  const currentPhrase = scaleInfoMap[value]?.phrase || (loadingDescriptions ? 'Loading...' : 'Description unavailable');
  const currentLifeApplication = scaleInfoMap[value]?.lifeApplication || '';

  return (
    <div className="w-full max-w-3xl mx-auto p-4 bg-white rounded-lg shadow-sm">
       <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Missional Scale Position
          {isSaving && <span className="ml-2 text-sm text-gray-500 animate-pulse">Saving...</span>}
       </h3>
       <h3 className="text-sm text-gray-400 mb-6">Pick the phrase that best describes your disciple's current position right now.</h3>
      
       {error && (
           <div className="mb-4 text-red-600 text-sm text-center">{error}</div>
       )}
       {loadingDescriptions && !error && (
           <div className="mb-4 text-gray-500 text-sm text-center">Loading scale descriptions...</div>
       )}

       {/* Main Vertical Layout Container - Added items-stretch */}
       <div className={`flex space-x-4 rounded-md items-stretch ${loadingDescriptions ? 'opacity-50' : ''}`}> 
           {/* Left Column: Vertical Slider - Reduced width to w-8 */}
           <div 
             className="flex justify-center items-center w-8 p-1 rounded-md" // Reduced width to w-8
             style={{ background: 'linear-gradient(to bottom, #0076BE, #85C123, #f7e509)' }}
           > 
             {/* Slider input - Removed h-full */}
             <input
                 type="range"
                 min="-10"
                 max="9"
                 step="1"
                 value={value}
                 onChange={handleChange}
                 disabled={loadingDescriptions}
                 // Apply styles for vertical appearance
                 // NOTE: Larger thumb requires custom CSS targeting ::-webkit-slider-thumb / ::-moz-range-thumb
                 className="appearance-none w-4 rounded-full cursor-grab active:cursor-grabbing orientation-vertical accent-blue-600"
                 style={{ writingMode: 'vertical-lr', direction: 'ltr' }} // Use ltr for top-to-bottom with range input
             />
           </div>
 
           {/* Right Column: Descriptions - Reduced padding p-2, reduced spacing space-y-0.5 */}
           <div className="flex-1 p-2 rounded-md space-y-0.5 bg-gradient-to-b from-blue-100 via-green-100 to-yellow-100"> {/* Reduced padding/spacing */} 
             {sortedLevels.map((level) => (
                 <div 
                     key={level} 
                     className={`py-0.5 px-1 rounded text-sm cursor-pointer transition-colors duration-150 hover:bg-black hover:bg-opacity-5 active:bg-opacity-10`}
                     onClick={() => handleLevelClick(level)} // Keep onClick on the outer div
                 >
                     {/* Use Flexbox to align number and phrase */}
                     <div className="flex items-center gap-x-2">
                         {/* Fixed-width, right-aligned span for the number */}
                         <span className="w-8 text-right text-gray-500 font-medium">
                             {level}
                         </span>
                         {/* Span for the phrase, apply conditional styling here */}
                         <span className={`${level === value ? 'font-bold text-blue-800' : 'text-gray-700'}`}>
                             {scaleInfoMap[level]?.phrase || '...'}
                         </span>
                     </div>
                 </div>
             ))}
           </div>
       </div>

       {/* Section Below: Current Level and Life Application */}
       {!loadingDescriptions && (
           <div className="mt-6 pt-4 border-t">
               <div className="text-center mb-2">
                   <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800`}>
                       Selected Level: {value}
                   </span>
                   <p className="text-gray-800 text-center font-semibold mt-2">
                       {currentPhrase}
                   </p>
               </div>
               <h4 className="text-sm font-medium text-gray-600 mb-1">Life Application:</h4>
               <p className="text-gray-700 text-sm">
                   {currentLifeApplication || 'No specific application guidance available for this level.'}
               </p>
           </div>
       )}
    </div>
  );
};

export default MissionalScale;
/* 
Add this CSS to your global stylesheet or component-specific CSS file 
for a larger, circular slider thumb:

input[type="range"].orientation-vertical::-webkit-slider-thumb {
  appearance: none; 
  height: 30px; 
  width: 30px; 
  background: #2563eb; // Example blue color
  border-radius: 50%;
  cursor: grab;
}

input[type="range"].orientation-vertical::-moz-range-thumb {
  appearance: none;
  height: 30px;
  width: 30px;
  background: #2563eb; // Example blue color
  border-radius: 50%;
  cursor: grab;
  border: none; // Often needed for Firefox
}

input[type="range"].orientation-vertical:active::-webkit-slider-thumb {
  cursor: grabbing;
}

input[type="range"].orientation-vertical:active::-moz-range-thumb {
  cursor: grabbing;
}

*/ 